import { startOfDay, startOfMonth, startOfWeek } from "date-fns";
import { computed, InjectionKey, Ref, ref, watch } from "vue";

import { useBrowserStore } from "@/composables/useBrowserStore";
import type { UseProfile } from "@/composables/useProfile";

/**
 * The layouts the planner can display. Maps onto the
 * `isWeekView`/`isThreeDayView`/`isWeekendShown` triplet used internally.
 *
 * The `N-day` layouts (except `5-day`) are "rolling" views: a custom time grid
 * that starts at the selected day and shows N consecutive days. `5-day` and
 * `week` are week-anchored views (weekdays vs full week).
 */
export type PlannerLayout =
  | "1-day"
  | "2-day"
  | "3-day"
  | "4-day"
  | "5-day"
  | "6-day"
  | "week"
  | "month";

export type PlannerViewState = {
  isWeekView: boolean;
  // True for rolling, custom-duration time-grid views (1/2/3/4/6-day).
  isThreeDayView: boolean;
  isWeekendShown: boolean;
  // Number of consecutive days shown by a rolling view (when `isThreeDayView`
  // is true). Ignored by the week (5-day / week) and month layouts.
  customDays: number;
};

export const layoutToViewState = (layout: PlannerLayout): PlannerViewState => {
  switch (layout) {
    case "5-day":
      return {
        isWeekView: true,
        isThreeDayView: false,
        isWeekendShown: false,
        customDays: 5,
      };
    case "week":
      return {
        isWeekView: true,
        isThreeDayView: false,
        isWeekendShown: true,
        customDays: 7,
      };
    case "month":
      return {
        isWeekView: false,
        isThreeDayView: false,
        isWeekendShown: false,
        customDays: 0,
      };
    default: {
      // Rolling N-day views (1/2/3/4/6-day). Weekends must stay visible,
      // otherwise a weekend day would be dropped from the consecutive span.
      const customDays = Number.parseInt(layout, 10);
      return {
        isWeekView: true,
        isThreeDayView: true,
        isWeekendShown: true,
        customDays,
      };
    }
  }
};

/** Canonical order used when rendering the layout selector. */
export const PLANNER_LAYOUTS: PlannerLayout[] = [
  "1-day",
  "2-day",
  "3-day",
  "4-day",
  "5-day",
  "6-day",
  "week",
  "month",
];

export type PlannerLayoutShortcuts = PlannerLayout[];

export const DEFAULT_LAYOUT_SHORTCUTS: PlannerLayoutShortcuts = [
  "3-day",
  "week",
  "month",
];

const parseLayout = (value: unknown): PlannerLayout | null => {
  switch (value) {
    case "1-day":
    case "2-day":
    case "3-day":
    case "4-day":
    case "5-day":
    case "6-day":
    case "week":
    case "month":
      return value;
    default:
      return null;
  }
};

/** Discard persisted shortcuts unless the complete value is valid. */
export const normalizeLayoutShortcuts = (
  value: unknown
): PlannerLayoutShortcuts => {
  if (!Array.isArray(value) || value.length === 0) {
    return [...DEFAULT_LAYOUT_SHORTCUTS];
  }

  const layouts: PlannerLayout[] = [];
  for (const entry of value) {
    const layout = parseLayout(entry);
    if (!layout) return [...DEFAULT_LAYOUT_SHORTCUTS];
    layouts.push(layout);
  }
  return layouts;
};

/** Which layouts the selector exposes. Omitted/false keys are hidden. */
export type PlannerSelectableLayouts = Partial<Record<PlannerLayout, boolean>>;

/** Layouts shown in the selector by default. */
export const DEFAULT_SELECTABLE_LAYOUTS: PlannerSelectableLayouts = {
  "1-day": true,
  "2-day": true,
  "3-day": true,
  "4-day": true,
  "5-day": true,
  "6-day": true,
  week: true,
  month: true,
};

/** Human-readable label for a layout button. */
export const layoutLabel = (layout: PlannerLayout): string => {
  switch (layout) {
    case "week":
      return "Week";
    case "month":
      return "Month";
    default:
      return layout; // "1-day", "2-day", ...
  }
};

/** Derive the active layout from the internal view-state booleans. */
export const viewStateToLayout = (state: PlannerViewState): PlannerLayout => {
  if (!state.isWeekView) return "month";
  // Rolling views encode their span in `customDays` (1/2/3/4/6).
  if (state.isThreeDayView) {
    switch (state.customDays) {
      case 1:
        return "1-day";
      case 2:
        return "2-day";
      case 4:
        return "4-day";
      case 6:
        return "6-day";
      default:
        return "3-day";
    }
  }
  return state.isWeekendShown ? "week" : "5-day";
};

export const formatCalendarDate = (
  date: Date,
  isWeekStartOnSunday: Ref<boolean>,
  viewState: { isThreeDayView: Ref<boolean>; isWeekView: Ref<boolean> }
): Date => {
  const { isThreeDayView, isWeekView } = viewState;
  if (isThreeDayView.value) return startOfDay(date);
  return isWeekView.value
    ? startOfWeek(date, {
        weekStartsOn: isWeekStartOnSunday.value ? 0 : 1,
      })
    : startOfMonth(date);
};

type LeftPaneState = "shown" | "hidden";

export const useLeftPane = (
  profile: Ref<UseProfile>,
  storageKey: string,
  updateCalendarSize: () => void
) => {
  // Load & sync left pane state with the browser's local storage.
  const { syncState } = useBrowserStore(profile.value.user.accountId);
  const leftPaneState = syncState<LeftPaneState>(storageKey, "shown");

  const isTemporaryShown = ref(leftPaneState.value === "shown");
  const isLeftPaneShown = computed(
    () => leftPaneState.value === "shown" || isTemporaryShown.value
  );

  // Watch for changes and update the calendar panes accordingly.
  const stopSizeWatcher = watch(isLeftPaneShown, updateCalendarSize);

  const hideLeftPane = () => {
    isTemporaryShown.value = false;
    leftPaneState.value = "hidden";
  };
  const showLeftPane = (isTemporary = false) => {
    if (isTemporary) {
      // Overlay the pane (e.g. event details) without persisting "shown" to storage.
      isTemporaryShown.value = true;
    } else {
      isTemporaryShown.value = false;
      leftPaneState.value = "shown";
    }
  };

  return {
    hideLeftPane,
    isLeftPaneShown,
    leftPaneState,
    showLeftPane,
    // Stops the persistence and size watchers; the calling component owns it
    // (`releaser.defer(release)`).
    release: () => {
      stopSizeWatcher();
      leftPaneState.release();
    },
  };
};

type UseLeftPane = {
  hideLeftPane: () => void;
  isLeftPaneShown: Ref<boolean>;
  showLeftPane: (isTemporary?: boolean) => void;
};

export const keyUseLeftPane: InjectionKey<UseLeftPane> = Symbol("useLeftPane");
